import React, { Fragment, useState } from "react";
import { closeModal } from "../../store/modal/modalReducer";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import { Dialog, Transition } from "@headlessui/react";
import ErrorBoundary from "../../containers/ErrorBoundary";

export default function ModalEditLinkedChat({ chat, current_booking, onUpdate }) {
    const dispatch = useDispatch();
    const { current_staff } = useSelector((state) => state.auth);
    const [open] = useState(true);
    const [saving, setSaving] = useState(false);
    
    // Determine which type belongs to current booking
    const isCurrentBooking = chat?.booking?.id === current_booking?.id;
    const [currentType, setCurrentType] = useState(isCurrentBooking ? chat?.type : chat?.linked_type);
    const [linkedType, setLinkedType] = useState(isCurrentBooking ? chat?.linked_type : chat?.type);

    async function saveChanges() {
        setSaving(true);
        
        // Map back to the correct fields based on which booking is current
        const updateData = isCurrentBooking 
            ? { type: currentType, linked_type: linkedType, staff_id: current_staff.id }
            : { type: linkedType, linked_type: currentType, staff_id: current_staff.id };

        await axios
            .put(`${process.env.REACT_APP_API_URL}update_linked_chat/${chat.id}`, updateData)
            .then(() => {
                if (onUpdate) {
                    onUpdate();
                }
                dispatch(closeModal());
            })
            .catch((error) => {
                console.log(error);
                alert("Failed to update linked chat");
            })
            .finally(() => {
                setSaving(false);
            });
    }

    const typeOptions = process.env.REACT_APP_COLOUR === "UC" 
        ? ["Student", "Parent", "Funder", "Sponsor", "Room Mate", "Other"]
        : process.env.REACT_APP_COLOUR === "QP"
        ? ["Main Applicant", "Co-Applicant", "Lease Holder", "Tenant", "Nominee", "Other"]
        : ["Main Applicant", "Co-Applicant", "Lease Holder", "Tenant", "Other"];

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog
                as="div"
                static
                className="fixed z-50 inset-0 overflow-y-auto"
                open={open}
                onClose={() => dispatch(closeModal())}
            >
                <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    <span
                        className="hidden sm:inline-block sm:align-middle sm:h-screen"
                        aria-hidden="true"
                    >
                        &#8203;
                    </span>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <ErrorBoundary>
                            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-auto shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                                <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                                    <button
                                        type="button"
                                        className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-0"
                                        onClick={() => dispatch(closeModal())}
                                    >
                                        <span className="sr-only">Close</span>
                                        <FontAwesomeIcon
                                            className="h-6 w-6"
                                            aria-hidden="true"
                                            icon={faTimes}
                                        />
                                    </button>
                                </div>

                                <div className="pr-8 w-full">
                                    <div className="mt-3 text-center sm:mt-0 sm:text-left">
                                        <Dialog.Title
                                            as="h1"
                                            className="text-lg font-medium text-gray-900"
                                        >
                                            Edit Linked Chat Types
                                        </Dialog.Title>

                                        <div className="mt-5 space-y-4">
                                            {/* Current Booking Type */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    {isCurrentBooking 
                                                        ? `${chat?.user?.name || ""} ${chat?.user?.surname || ""}`.trim()
                                                        : `${chat?.linked_user?.name || ""} ${chat?.linked_user?.surname || ""}`.trim()
                                                    } ({current_booking?.booking_reference})
                                                </label>
                                                <select
                                                    value={currentType}
                                                    onChange={(e) => setCurrentType(e.target.value)}
                                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                                                >
                                                    {typeOptions.map((option) => (
                                                        <option key={option} value={option}>
                                                            {option}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            {/* Linked Booking Type */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    {isCurrentBooking 
                                                        ? `${chat?.linked_user?.name || ""} ${chat?.linked_user?.surname || ""}`.trim()
                                                        : `${chat?.user?.name || ""} ${chat?.user?.surname || ""}`.trim()
                                                    } ({isCurrentBooking ? chat?.linked_booking?.booking_reference : chat?.booking?.booking_reference})
                                                </label>
                                                <select
                                                    value={linkedType}
                                                    onChange={(e) => setLinkedType(e.target.value)}
                                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                                                >
                                                    {typeOptions.map((option) => (
                                                        <option key={option} value={option}>
                                                            {option}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        <div className="mt-6 flex justify-end gap-3">
                                            <button
                                                onClick={() => dispatch(closeModal())}
                                                className="text-gray-700 uppercase px-4 py-2 rounded-lg border text-sm focus:z-10 focus:outline-none border-gray-300 bg-gray-100 hover:bg-gray-200 focus:ring-gray-300 focus:ring-2 focus:ring-offset-2 transition duration-150 ease-in-ease-out hover:-translate-y-0.5"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={saveChanges}
                                                disabled={saving}
                                                className="text-white uppercase px-4 py-2 rounded-lg border text-sm focus:z-10 focus:outline-none border-primary bg-primary hover:bg-primary_hover focus:ring-primary focus:ring-2 focus:ring-offset-2 transition duration-150 ease-in-ease-out hover:-translate-y-0.5 disabled:opacity-50"
                                            >
                                                {saving ? "Saving..." : "Save"}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </ErrorBoundary>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
